/**
 * VideoPreview Component
 *
 * Enhanced video player with:
 * - R7: Timestamp markers synced to Gemini findings
 * - R9: Keyboard shortcuts (Space, Arrow keys, J/K/L)
 *
 * Mode: Deep Analysis (Mode 3)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

/**
 * Parse a timestamp string like "0:04" or "1:23" into seconds.
 */
function parseTimestamp(ts) {
  if (!ts) return 0;
  const parts = ts.split(':').map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

export const VideoPreview = ({ file, className, findings = [], onSeekToTimestamp }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      setIsLoading(false);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const seekTo = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [duration]);

  const handleTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    seekTo(pct * duration);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  // R9: Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle when this component is focused or its container
      if (!containerRef.current?.contains(document.activeElement) &&
          document.activeElement !== document.body) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekTo(currentTime - 5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekTo(currentTime + 5);
          break;
        case 'j':
        case 'J':
          e.preventDefault();
          seekTo(currentTime - 10);
          break;
        case 'k':
        case 'K':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'l':
        case 'L':
          e.preventDefault();
          seekTo(currentTime + 10);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePlayPause, seekTo, currentTime]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i];
  };

  // Convert findings to marker positions
  const markers = findings.map(f => ({
    time: parseTimestamp(f.timestamp),
    severity: f.severity,
    description: f.description,
    timestamp: f.timestamp,
  })).filter(m => m.time <= duration);

  if (isLoading) {
    return (
      <div className={`bg-[#0b1118] border border-white/10 rounded-xl p-4 flex items-center justify-center h-48 ${className || ''}`}>
        <div className="text-zinc-500">Loading video preview...</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={`bg-[#0b1118] border border-white/10 rounded-xl overflow-hidden transition-all duration-300 hover:border-white/20 focus:outline-none focus:ring-1 focus:ring-orange-400/50 ${className || ''}`}
    >
      {/* Video Player */}
      <div className="relative aspect-video bg-black group">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />

        {/* Play/Pause Overlay */}
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          onClick={handlePlayPause}
        >
          <button className="bg-black/60 hover:bg-black/80 text-white border-none rounded-full p-4">
            {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="p-4 space-y-3">
        {/* Progress Bar with Timestamp Markers (R7) */}
        <div className="space-y-1">
          <div
            className="relative h-2 bg-white/10 rounded-full cursor-pointer group/progress"
            onClick={handleSeek}
          >
            {/* Progress fill */}
            <div
              className="absolute top-0 left-0 h-full bg-orange-400 rounded-full"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            />

            {/* R7: Timestamp markers */}
            {markers.map((marker, i) => {
              const pct = duration ? (marker.time / duration) * 100 : 0;
              const markerColor =
                marker.severity === 'error' ? 'bg-red-500' :
                marker.severity === 'warning' ? 'bg-amber-400' :
                'bg-blue-400';
              return (
                <button
                  key={i}
                  className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${markerColor} border border-white/40 hover:scale-150 transition-transform z-10`}
                  style={{ left: `${pct}%`, marginLeft: '-6px' }}
                  title={`${marker.timestamp}: ${marker.description}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    seekTo(marker.time);
                    if (onSeekToTimestamp) onSeekToTimestamp(marker.timestamp);
                  }}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePlayPause}
              className="text-zinc-300 hover:text-orange-400 p-1"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>

            <button
              onClick={toggleMute}
              className="text-zinc-300 hover:text-orange-400 p-1"
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>

            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => {
                const v = Number(e.target.value);
                setVolume(v);
                if (videoRef.current) videoRef.current.volume = v;
                setIsMuted(v === 0);
              }}
              className="w-16 accent-orange-400"
            />
          </div>

          <span className="text-xs text-zinc-500 rounded-full px-2 py-0.5 bg-white/5 border border-white/10">
            {formatFileSize(file.size)}
          </span>
        </div>

        {/* File Info */}
        <div className="text-xs text-zinc-500 border-t border-white/10 pt-2">
          <div className="truncate font-medium text-zinc-300">{file.name}</div>
          <div className="flex items-center gap-3 mt-1">
            <span>Type: {file.type}</span>
            {markers.length > 0 && (
              <span className="text-orange-400">{markers.length} finding{markers.length !== 1 ? 's' : ''} marked</span>
            )}
          </div>
        </div>

        {/* R9: Keyboard shortcut hint */}
        <div className="text-[10px] text-zinc-600 text-center">
          Space: play/pause | Arrow keys: +/-5s | J/K/L: review controls
        </div>
      </div>
    </div>
  );
};
